import React from 'react';
import '../styles/SubjectPropertyAnalysis.css';
import Badge from './Badge';

function SubjectPropertyAnalysis(props) {
    const subjectPropertyAnalysis = props.subjectPropertyAnalysis;
    const darkMode = props.darkMode || false;
    const showScrollableImages = props.showScrollableImages;

    if (!subjectPropertyAnalysis) {
        return null;
    }

    const textColorClass = darkMode ? 'colour-white' : 'colour-primary';
    const containerClass = darkMode ? 'bg-colour-dark-mode' : 'bg-colour-white';
    
    // Helper function to convert rule keys to human-readable labels (from CompTracking.js)
    const getHumanReadableRuleLabel = (ruleKey) => {
        if (!ruleKey || typeof ruleKey !== 'string') return ruleKey;
        
        // Remove prefixes like "global:", "market:", "tier1:", "tier2:", "tier3:"
        const cleanRule = ruleKey.replace(/^(global|market):(tier[123]:)?/, '');
        
        // Rule label mappings
        const ruleLabels = {
            // Global positive drivers
            'waterfront_frontage_prime': 'Prime Waterfront Frontage',
            'golf_frontage_prime': 'Prime Golf Frontage',
            'golf_frontage_prime_down_the_hole': 'Prime Golf Frontage (Down-the-Hole)',
            'waterfront_frontage_secondary': 'Secondary Waterfront Frontage',
            'golf_frontage_nonprime': 'Non-Prime Golf Frontage',
            'adjacent_park_or_greenbelt': 'Adjacent Park or Greenbelt',
            'backs_to_natural_open_space': 'Backs to Natural Open Space',
            'broad_mountain_view': 'Broad Mountain View',
            'waterfront_view_only': 'Waterfront View Only',
            'nearby_park_or_greenbelt': 'Nearby Park or Greenbelt',
            'partial_mountain_view': 'Partial Mountain View',
            'extra_lot_privacy_or_buffer': 'Extra Lot Privacy or Buffer',
            'cul_de_sac_setting': 'Cul-de-Sac',

            // Global negative drivers
            'attached_cluster_micro_lot': 'Attached Cluster/Micro Lot',
            'adjacent_heavy_commercial_or_parking': 'Adjacent Heavy Commercial or Parking',
            'adjacent_industrial_or_utility_site': 'Adjacent Industrial or Utility Site',
            'overhead_high_voltage_corridor': 'Overhead High Voltage Corridor',
            'terrain_hazard_slope_or_cliff': 'Terrain Hazard (Slope or Cliff)',
            'flood_control_basin_or_water_hazard': 'Flood Control Basin or Water Hazard',
            'backs_to_engineered_canal_or_drainage': 'Backs to Engineered Canal or Drainage',
            'nearby_ag_or_industrial_operations': 'Nearby Agricultural or Industrial Operations',
            'visual_blight_billboards_or_wires': 'Visual Blight (Billboards or Wires)',
            'flag_lot_setting': 'Flag Lot',
            'access_constraint_shared_easement': 'Access Constraint (Shared Easement/Drive)',
            'access_constraint_flag_lot_or_easement': 'Access Constraint (Flag Lot or Easement)', // legacy — kept for historic Firestore docs
            'access_constraint_limited_parking_or_grade': 'Access Constraint (Limited Parking or Grade)',
            't_intersection_head_on': 'T-Intersection (Head-On)',
            'backs_to_multifamily': 'Backs to Multifamily',

            // Noise rules
            'AVOID_SUBJECT_HIGH_vs_COMP_LOW': 'Avoid: Subject High Noise vs Comp Low Noise',
            'AVOID_SUBJECT_LOW_vs_COMP_HIGH': 'Avoid: Subject Low Noise vs Comp High Noise',
            'subject_noise_low_vs_comp_medium': 'Subject Low Noise vs Comp Medium Noise',
            'subject_noise_medium_vs_comp_low_or_high': 'Subject Medium Noise vs Comp Low/High Noise',
            'noise_within_one_bucket_and_adjust': 'Noise Within One Level',
            
            // Curb appeal rules
            'subject_curb_high_vs_comp_low': 'Subject High Curb Appeal vs Comp Low',
            'curbAppeal_low': 'Low Curb Appeal',
            'curbAppeal_medium': 'Medium Curb Appeal',
            'curbAppeal_high': 'High Curb Appeal',
            
            // Market rules
            'floodZoneCategory_match_if_possible': 'Flood Zone Category Match',
            'poolPresence_equal_if_possible_high_priority': 'Pool Presence Match',
            'waterfront_vs_non_waterfront_match': 'Waterfront vs Non-Waterfront Match',
            'deep_open_waterfront_vs_shallow_creek_match': 'Deep Open Waterfront vs Shallow Creek Match',
            'structureType_block_vs_frame_match': 'Structure Type Match (Block vs Frame)',
            'stories_match_one_vs_two_where_possible': 'Stories Match (One vs Two)',
            'masterPlannedCommunity_with_HOA_amenities_match': 'Master Planned Community with HOA Match',
            'pool_value_adjustment_when_forced_to_mismatch': 'Pool Value Adjustment (Forced Mismatch)',
            'fine_grained_view_quality_within_same_waterfront_class': 'Fine-Grained View Quality (Same Waterfront Class)',
            'broad_mountain_view_match_for_subject_with_premium_view': 'Broad Mountain View Match (Premium View)',
            'unobstructed_golf_frontage_match': 'Unobstructed Golf Frontage Match',
            'unobstructed_waterfront_match': 'Unobstructed Waterfront Match',
            'basementPresence_match (full/partial vs none/slab)': 'Basement Presence Match',
            'brickFacade_and_preserved_millwork_vs_plain_frame_match': 'Brick Facade and Preserved Millwork vs Plain Frame Match',
            'driveway_or_frontGarage_presence_adjustable_premium': 'Driveway or Front Garage Presence (Adjustable Premium)',
            'poolPresence_adjustable_20k_to_30k': 'Pool Presence Adjustment ($20k-$30k)',
            'floodZone': 'Flood Zone Match',
            'basementPresence': 'Basement Presence Match',
            'premiumSubdivisionMatch': 'Premium Subdivision Match',
            'worstMaterial': 'Worst Construction Material',
            'missingPreferredMaterial': 'Missing Preferred Construction Material'
        };
        
        // Try exact match first
        if (ruleLabels[cleanRule]) {
            return ruleLabels[cleanRule];
        }
        
        // Try to find partial match
        for (const [key, label] of Object.entries(ruleLabels)) {
            if (cleanRule.includes(key) || key.includes(cleanRule)) {
                return label;
            }
        }
        
        // Fallback: format the rule key nicely
        return cleanRule
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/\b\w/g, l => l.toUpperCase())
            .trim();
    };
    
    // Extract analysis text
    const getAnalysisText = () => {
        if (!subjectPropertyAnalysis.analysis) return '';
        
        const analysis = subjectPropertyAnalysis.analysis;
        if (typeof analysis === 'string') {
            return analysis;
        } else if (analysis && typeof analysis === 'object') {
            if (analysis.text) {
                return analysis.text;
            } else if (analysis.content) {
                return analysis.content;
            } else {
                const keys = Object.keys(analysis);
                if (keys.length === 1 && typeof analysis[keys[0]] === 'string') {
                    return analysis[keys[0]];
                }
            }
        }
        return String(analysis || '');
    };

    // Extract recommendation text
    const getRecommendationText = () => {
        if (!subjectPropertyAnalysis.recommendation) return '';
        
        const recommendation = subjectPropertyAnalysis.recommendation;
        if (typeof recommendation === 'string') {
            return recommendation;
        } else if (recommendation && typeof recommendation === 'object') {
            if (recommendation.text) {
                return recommendation.text;
            } else if (recommendation.content) {
                return recommendation.content;
            } else {
                const keys = Object.keys(recommendation);
                if (keys.length === 1 && typeof recommendation[keys[0]] === 'string') {
                    return recommendation[keys[0]];
                }
            }
        }
        return String(recommendation || '');
    };

    const analysisText = getAnalysisText();
    const recommendationText = getRecommendationText();
    const subjectImages = subjectPropertyAnalysis.subjectImages || [];
    
    // Get positive and negative drivers
    const positiveDrivers = subjectPropertyAnalysis.positiveDrivers || [];
    const negativeDrivers = subjectPropertyAnalysis.negativeDrivers || [];
    const noiseRating = subjectPropertyAnalysis.noiseRating;
    const curbAppealRating = subjectPropertyAnalysis.curbAppealRating;
    
    // Build list of good and bad tags
    const goodTags = [...positiveDrivers];
    const badTags = [...negativeDrivers];
    
    // Add noise rating if high (bad tag)
    if (noiseRating && noiseRating.toLowerCase() === 'high') {
        badTags.push('noise_high');
    }
    
    // Add curb appeal rating if low (bad tag)
    if (curbAppealRating && curbAppealRating.toLowerCase() === 'low') {
        badTags.push('curbAppeal_low');
    }

    // Handle image click - create a property-like object for showScrollableImages
    const handleImageClick = () => {
        if (showScrollableImages && subjectImages.length > 0) {
            // Create a property-like object that matches what showScrollableImages expects
            const propertyObject = {
                listingDetails: {
                    mlsPhotoUrls: subjectImages
                }
            };
            showScrollableImages(propertyObject);
        }
    };

    return (
        <div className={`subject-property-analysis-container ${containerClass}`}>
            {/* Header Section */}
            <div className="subject-property-analysis-header-section">
                <h3 className={`heading-small-bold ${textColorClass}`}>
                    Subject property
                </h3>
                {analysisText && (
                    <p className={`body-regular ${textColorClass}`}>
                        {analysisText.replace(/\*\*/g, '')}
                    </p>
                )}
            </div>

            {/* Images Section */}
            {subjectImages.length > 0 && (
                <div className="subject-property-analysis-images-section">
                    {subjectImages.map((imageUrl, index) => (
                        <div 
                            key={index} 
                            className={`subject-property-analysis-image-container ${showScrollableImages ? 'cursor-pointer' : ''}`}
                            onClick={showScrollableImages ? handleImageClick : undefined}
                        >
                            <img
                                src={imageUrl}
                                alt={`Subject property ${index + 1}`}
                                className="subject-property-analysis-image"
                                loading="lazy"
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Recommendation Section */}
            {(recommendationText || goodTags.length > 0 || badTags.length > 0) && (
                <div className="subject-property-analysis-recommendation-section">
                    {recommendationText && (
                        <p className={`body-regular ${textColorClass}`}>
                            {recommendationText.replace(/\*\*/g, '')}
                        </p>
                    )}
                    {(goodTags.length > 0 || badTags.length > 0) && (
                        <>
                            <div className="subject-property-analysis-factors-columns">
                                {goodTags.length > 0 && (
                                    <div className="subject-property-analysis-factors-column">
                                        <p className={`body-bold ${textColorClass} subject-property-analysis-column-heading`}>
                                            Positive factors
                                        </p>
                                        <div className="subject-property-analysis-badges-container">
                                            {goodTags.map((tag, idx) => {
                                                const label = getHumanReadableRuleLabel(tag);
                                                return (
                                                    <Badge
                                                        key={idx}
                                                        text={label}
                                                        bgColour="bg-colour-jade-01"
                                                        textClass="body-regular colour-primary"
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {badTags.length > 0 && (
                                    <div className="subject-property-analysis-factors-column">
                                        <p className={`body-bold ${textColorClass} subject-property-analysis-column-heading`}>
                                            Negative factors
                                        </p>
                                        <div className="subject-property-analysis-badges-container">
                                            {badTags.map((tag, idx) => {
                                                let label = getHumanReadableRuleLabel(tag);
                                                // Handle special cases for noise and curb appeal
                                                if (tag === 'noise_high') {
                                                    label = 'Noise';
                                                } else if (tag === 'curbAppeal_low') {
                                                    label = 'Low Curb Appeal';
                                                }
                                                return (
                                                    <Badge
                                                        key={idx}
                                                        text={label}
                                                        bgColour="bg-colour-red-01"
                                                        textClass="body-regular colour-primary"
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default SubjectPropertyAnalysis;

